import { useState } from 'react';

import { SnackbarTypeSeverity } from '../../../constants/types';
import { IRequest } from './types';

type Snackbar = (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;

/**
 * SHER-366 — shared orchestration for the modify/cancel-request mutations.
 *
 * The three actions (add-service / remove-service / cancel) ran the exact same
 * loading → call → snackbar → propagate dance; this collapses it to one place so
 * the error/success UX can't drift between them. `loading` is exposed so callers
 * can disable the confirm control while a mutation is in flight (double-submit
 * guard), and `run` resolves to whether the mutation succeeded so the caller can
 * close its modal on success (and leave it open, with the entered values, on
 * error).
 */
export function useRequestMutation(setShowSnackbar: Snackbar, onChanged: (updated: IRequest) => void) {
    const [loading, setLoading] = useState(false);

    const run = async (call: () => Promise<{ request?: IRequest }>, successKey: string): Promise<boolean> => {
        setLoading(true);
        try {
            const response = await call();
            if (response.request) {
                onChanged(response.request);
            }
            setShowSnackbar({ show: true, message: successKey, severity: SnackbarTypeSeverity.SUCCESS });
            return true;
        } catch (error: any) {
            setShowSnackbar({ show: true, message: error?.errorMessage || 'general.error', severity: SnackbarTypeSeverity.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    };

    return { loading, run };
}
